import type { AccessibilityFilters, AccessibilityFilterTag, FilterSectionResult, GuideFilterSection } from "common/types";
import { parseKey, type SelectionKey } from "common/utils/accessibilitySelection";

interface SpaceLike {
  level_tags_filter_options?: {
    questionOptions?: { tagOptions?: { optionTitleId?: number | string | null }[] | null }[] | null;
  } | null;
}

/** The guide's filter sections when the API sent them (an empty list included), else null for today's A–Z list. */
export function guideSections(filters: AccessibilityFilters | null | undefined): GuideFilterSection[] | null {
  // Without view permission the API sends `accessibility_filters: []`.
  const sections = (filters as Partial<AccessibilityFilters> | null | undefined)?.sections;
  return Array.isArray(sections) ? sections : null;
}

/** The tag ids a space recorded. */
export function spaceTagIds(space: SpaceLike): number[] {
  const ids = new Set<number>();
  for (const question of space.level_tags_filter_options?.questionOptions ?? []) {
    for (const tag of question.tagOptions ?? []) {
      const id = Number(tag.optionTitleId);
      if (Number.isInteger(id) && id > 0) ids.add(id);
    }
  }
  return [...ids];
}

const areaIndexCache = new WeakMap<GuideFilterSection[], Map<SelectionKey, number[]>>();

function areaIndex(sections: GuideFilterSection[]): Map<SelectionKey, number[]> {
  let index = areaIndexCache.get(sections);
  if (!index) {
    index = new Map();
    for (const section of sections) {
      for (const tag of section.accessibility_tags) index.set(`${section.id}:${tag.id}`, tag.area_ids);
    }
    areaIndexCache.set(sections, index);
  }
  return index;
}

/** The ticked keys a space in `areaId` satisfies: its section lists the area for the tag, and the space recorded it. */
export function matchedKeys(
  areaId: number,
  tagIds: number[],
  keys: SelectionKey[],
  sections: GuideFilterSection[],
): SelectionKey[] {
  const index = areaIndex(sections);
  return keys.filter((key) => {
    const parsed = parseKey(key);
    return parsed !== null && tagIds.includes(parsed.tagId) && (index.get(key) ?? []).includes(areaId);
  });
}

/** A space shows when it satisfies any ticked key. */
export function spaceMatches(
  areaId: number,
  tagIds: number[],
  keys: SelectionKey[],
  sections: GuideFilterSection[],
): boolean {
  return matchedKeys(areaId, tagIds, keys, sections).length > 0;
}

/** The feature name a key stands for; undefined for a key no section offers. */
export function keyTagName(key: SelectionKey, sections: GuideFilterSection[]): string | undefined {
  const parsed = parseKey(key);
  if (!parsed) return undefined;
  const section = sections.find((s) => s.id === parsed.headingId);
  return section?.accessibility_tags.find((tag) => tag.id === parsed.tagId)?.name;
}

/** Any positive id does: the fallback list is one section, and its keys collapse back to tag ids. */
export const FALLBACK_GUIDE_SECTION_ID = 1;

/** Today's A–Z list as one panel section, for a guide the API sent no sections for. */
export function fallbackGuideSection(tags: AccessibilityFilterTag[], name: string): FilterSectionResult {
  return {
    id: FALLBACK_GUIDE_SECTION_ID,
    name,
    // A tick covers this section only and no step-free sub-block applies, as for a venue's own sections.
    kind: "complex",
    accessibility_tags: tags
      .map((tag) => ({ id: tag.tag_id, name: tag.tag_name }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  };
}
